import type { AllInvite, TeamMember } from '@/types/session';

export type MemberPermission = TeamMember['permission']; // 'read' | 'read_write' | 'admin'

/**
 * Map a backend membership `role` to the UI `permission` vocabulary.
 * Convention: admin↔admin, member↔read_write, read_only↔read.
 * `owner` collapses to `admin` for display (owner is rendered distinctly elsewhere).
 */
export const roleToPermission = (role: string): MemberPermission =>
  role === 'admin' || role === 'owner'
    ? 'admin'
    : role === 'read_only'
      ? 'read'
      : 'read_write';

/**
 * Map a UI `permission` back to the canonical backend `role`
 * accepted by PATCH /workspaces/:ws/members/:id ({ role }).
 */
export const permissionToRole = (permission: MemberPermission): 'admin' | 'member' | 'read_only' =>
  permission === 'admin' ? 'admin' : permission === 'read' ? 'read_only' : 'member';

export const permissionLabel = (permission: MemberPermission): string =>
  permission === 'admin' ? 'Admin' : permission === 'read_write' ? 'Read & Write' : 'Read Only';

// Status display helpers - map 'accepted' to 'Active' for display
export const getStatusDisplay = (status: string) => {
  switch (status) {
    case 'accepted': return 'Active';
    case 'pending': return 'Pending';
    case 'rejected': return 'Blocked';
    case 'removed': return 'Removed';
    default: return status;
  }
};

// Sort invites: pending first, then accepted, then ignored
export const sortInvites = (allInvites: AllInvite[]): AllInvite[] =>
  [...allInvites].sort((a, b) => {
    const order = { pending: 0, accepted: 1, ignored: 2 };
    const aOrder = order[a.status as keyof typeof order] ?? 3;
    const bOrder = order[b.status as keyof typeof order] ?? 3;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return b.invitedAt - a.invitedAt;
  });
